import { ElementType, forwardRef, HTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import parse from 'html-react-parser'

import { nl2br, removeParagraph } from '@/utils/strings'

import { cn } from '@/lib/utils'

const typographyVariants = cva('', {
  variants: {
    variant: {
      big: 'text-s-big md:text-d-big',
      h1: 'text-s-h1 md:text-d-h1',
      h2: 'text-s-h2 md:text-d-h2',
      h3: 'text-s-h3 md:text-d-h3',
      h4: 'text-s-h4 md:text-d-h4',
      h5: 'text-s-h5 md:text-d-h5',
      h6: 'text-s-h6 md:text-d-h6',
      body: 'text-body',
      small: 'text-small',
      label: 'text-label'
    }
  },
  defaultVariants: {
    variant: 'body'
  }
})

const childrenClasses = cn([
  '[&_ul]:list-disc [&_ul]:pl-[1.45rem]',
  '[&_a]:underline [&_a:hover]:text-copper [&_a]:transition-colors [&_a]:duration-300 [&_a]:ease-out-cubic'
])

export type TypographyProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof typographyVariants> &
  Partial<{
    component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
    paragraph: boolean
    asChild: boolean
  }>

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant,
      className,
      children,
      component = false,
      paragraph = false,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild
      ? Slot
      : ((component || (paragraph ? 'p' : 'span')) as ElementType)

    return (
      <Component
        className={cn(
          typographyVariants({ variant, className }),
          childrenClasses
        )}
        ref={ref}
        {...props}
      >
        {parse(children ? removeParagraph(nl2br(children as string)) : '')}
      </Component>
    )
  }
)

export default Typography
